import React from 'react';
import Board from '../components/Board';
import BattlePanel from '../components/BattlePanel';
import GameSidebar from '../components/GameSidebar';
import StatusBar from '../components/StatusBar';
import { calculateBattleOutcome } from '../gameLogic/battles';
import { getAdjacentPositions } from '../gameLogic/board';
import { getInitialState, applyMove, applyBattleOutcome } from '../gameLogic/state';

export default class LocalGame extends React.Component {
  constructor(props) {
    super(props);
    this.state = getInitialState();
  }

  resetGame() {
    this.setState(getInitialState());
  }

  resolveBattle() {
    const { pendingBattle } = this.state;
    if (!pendingBattle) return;

    const { squares, playerPositions, moveHistory } = applyBattleOutcome(this.state, pendingBattle);

    this.setState({
      squares,
      playerPositions,
      turn: pendingBattle.nextTurn,
      phase: pendingBattle.nextPhase,
      selected: null,
      possibleMoves: Array(4).fill(null),
      pendingBattle: null,
      moveHistory,
    });
  }

  randomize() {
    const squares = this.state.squares.slice();
    const playerPositions = this.state.playerPositions.slice();

    const makePieces = () => ["5S", "4S", "3S", "2S", "1S", "C", "LC",
      "M", "CA", "1L", "2L", "S", "F", "SP", "SP",
      "P", "P", "P", "P", "P", "P"];

    const randomize27 = (piecesArr) => {
      const positions = Array(27).fill(null);
      while (piecesArr.length > 0) {
        const idx = Math.floor(Math.random() * 27);
        if (positions[idx] == null) {
          positions[idx] = piecesArr.shift();
        }
      }
      return positions;
    };

    if (this.state.phase === "player1_setup") {
      for (let i = 45; i < 72; i++) {
        squares[i] = null;
        playerPositions[i] = null;
      }
      const positions = randomize27(makePieces());
      for (let i = 0; i < positions.length; i++) {
        squares[i + 45] = positions[i];
        playerPositions[i + 45] = positions[i] != null ? "1" : null;
      }
      this.setState({
        turn: "2",
        squares,
        playerPositions,
        p1PiecesToPlace: [],
        phase: "player2_setup",
      });
    } else if (this.state.phase === "player2_setup") {
      for (let i = 0; i < 27; i++) {
        squares[i] = null;
        playerPositions[i] = null;
      }
      const positions = randomize27(makePieces());
      for (let i = 0; i < positions.length; i++) {
        squares[i] = positions[i];
        playerPositions[i] = positions[i] != null ? "2" : null;
      }
      this.setState({
        turn: "wait1",
        squares,
        playerPositions,
        p2PiecesToPlace: [],
        phase: "game",
      });
    }
  }

  handleClick(i) {
    const row = Math.floor(i / 9);
    const current = this.state;

    if (current.pendingBattle) return;

    const squares = current.squares.slice();
    const playerPositions = current.playerPositions.slice();

    if (current.phase === "player1_setup") {
      if (row > 4) {
        const player1pieces = [...current.p1PiecesToPlace];
        if (player1pieces.length > 0) {
          if (squares[i] == null) {
            squares[i] = player1pieces.shift();
            playerPositions[i] = "1";
          } else {
            player1pieces.unshift(squares[i]);
            squares[i] = null;
            playerPositions[i] = null;
          }
          this.setState({ squares, p1PiecesToPlace: player1pieces, playerPositions });
        } else {
          this.setState({ turn: "2", phase: "player2_setup" });
        }
      }
    } else if (current.phase === "player2_setup") {
      if (row < 3) {
        const player2pieces = [...current.p2PiecesToPlace];
        if (player2pieces.length > 0) {
          if (squares[i] == null) {
            squares[i] = player2pieces.shift();
            playerPositions[i] = "2";
          } else {
            player2pieces.unshift(squares[i]);
            squares[i] = null;
            playerPositions[i] = null;
          }
          this.setState({ squares, p2PiecesToPlace: player2pieces, playerPositions });
        } else {
          this.setState({ turn: "wait1", phase: "game" });
        }
      }
    } else if (current.phase === "game") {
      if (current.turn === "wait1") {
        this.setState({ turn: "1" });
      } else if (current.turn === "wait2") {
        this.setState({ turn: "2" });
      } else {
        const isAdjacent = current.possibleMoves.includes(i);

        if (isAdjacent) {
          if (squares[i] == null) {
            // Normal move to empty square
            const { squares: newSquares, playerPositions: newPositions, moveHistory } =
              applyMove(current, current.selected, i);
            this.setState({
              squares: newSquares,
              playerPositions: newPositions,
              turn: current.turn === "1" ? "wait2" : "wait1",
              selected: null,
              possibleMoves: Array(4).fill(null),
              moveHistory,
            });
          } else if (playerPositions[i] === playerPositions[current.selected]) {
            // Clicked own piece — deselect
            this.setState({ selected: null, possibleMoves: Array(4).fill(null) });
          } else {
            // Challenge
            const attackerPiece = squares[current.selected];
            const defenderPiece = squares[i];
            const attackerPlayer = current.turn;
            const defenderPlayer = attackerPlayer === "1" ? "2" : "1";
            const outcome = calculateBattleOutcome(attackerPiece, defenderPiece);

            const isGameOver = defenderPiece === "F" || attackerPiece === "F";
            let nextPhase = current.phase;
            let nextTurn;

            if (isGameOver) {
              nextPhase = "end";
              if (outcome === "tie") {
                nextTurn = "tie";
              } else {
                const winnerPlayer = outcome === "attacker" ? attackerPlayer : defenderPlayer;
                nextTurn = winnerPlayer === "1" ? "wait2" : "wait1";
              }
            } else {
              nextTurn = attackerPlayer === "1" ? "wait2" : "wait1";
            }

            this.setState({
              pendingBattle: {
                fromIdx: current.selected,
                toIdx: i,
                attackerPiece,
                defenderPiece,
                attackerPlayer,
                outcome,
                nextTurn,
                nextPhase,
              },
              selected: null,
              possibleMoves: Array(4).fill(null),
            });
          }
        } else {
          if (current.selected == null && squares[i] != null && playerPositions[i] === current.turn) {
            this.setState({
              selected: i,
              possibleMoves: getAdjacentPositions(i, squares, playerPositions),
            });
          } else {
            this.setState({ selected: null, possibleMoves: Array(4).fill(null) });
          }
        }
      }
    }
    // end phase: ignore clicks
  }

  surrender() {
    this.setState({
      phase: "end",
      turn: this.state.turn === "1" ? "wait1" : "wait2",
    });
  }

  render() {
    const { phase, turn, pendingBattle, moveHistory, p1PiecesToPlace, p2PiecesToPlace, playerPositions } = this.state;

    // --- Last opponent move (for highlights) ---
    let lastOpponentMove = null;
    if (phase === "game" && (turn === "1" || turn === "2")) {
      const opponentPlayer = turn === "1" ? "2" : "1";
      for (let i = moveHistory.length - 1; i >= 0; i--) {
        if (moveHistory[i].turn === opponentPlayer) {
          lastOpponentMove = {
            from: moveHistory[i].from,
            to: moveHistory[i].to,
            type: moveHistory[i].type,
            outcome: moveHistory[i].outcome,
          };
          break;
        }
      }
    }

    let lastMoveNote = null;
    if (lastOpponentMove) {
      if (lastOpponentMove.type === "move") {
        lastMoveNote = "Opponent's last move is highlighted";
      } else if (lastOpponentMove.outcome === "attacker") {
        lastMoveNote = "Opponent attacked and captured your piece — highlighted";
      } else if (lastOpponentMove.outcome === "defender") {
        lastMoveNote = "Opponent attacked — their piece was eliminated — origin highlighted";
      } else {
        lastMoveNote = "Opponent attacked — both pieces were eliminated — origin highlighted";
      }
    }

    // --- Board flip ---
    const flipped =
      phase === "player2_setup" ||
      turn === "2" ||
      turn === "wait2" ||
      (pendingBattle !== null && pendingBattle.attackerPlayer === "2");

    // --- Turn indicator ---
    let turnIndicatorText = null;
    let turnIndicatorClass = null;
    if (phase === "player1_setup" || turn === "1" || turn === "wait1") {
      turnIndicatorText = "Player 1";
      turnIndicatorClass = "turn-indicator player-1";
    } else if (phase === "player2_setup" || turn === "2" || turn === "wait2") {
      turnIndicatorText = "Player 2";
      turnIndicatorClass = "turn-indicator player-2";
    }

    return (
      <div className="game">
        <div className="game-board">
          {turnIndicatorText && (
            <div className={turnIndicatorClass}>{turnIndicatorText}</div>
          )}
          <StatusBar
            phase={phase}
            turn={turn}
            pendingBattle={pendingBattle}
            lastMoveNote={lastMoveNote}
            onNewGame={() => this.resetGame()}
          />
          <BattlePanel
            pendingBattle={pendingBattle}
            onResolve={() => this.resolveBattle()}
          />
          <Board
            squares={this.state.squares}
            onClick={(i) => this.handleClick(i)}
            possibleMoves={this.state.possibleMoves}
            playerPositions={this.state.playerPositions}
            player={this.state.turn}
            phase={phase}
            selected={this.state.selected}
            flipped={flipped}
            lastOpponentMove={lastOpponentMove}
          />
        </div>

        <GameSidebar
          phase={phase}
          turn={turn}
          selected={this.state.selected}
          squares={this.state.squares}
          moveHistory={moveHistory}
          playerPositions={playerPositions}
          p1PiecesToPlace={p1PiecesToPlace}
          p2PiecesToPlace={p2PiecesToPlace}
          onRandomize={() => this.randomize()}
          onSurrender={() => this.surrender()}
          onNewGame={() => this.resetGame()}
          onHome={this.props.onBack}
        />
      </div>
    );
  }
}
